import { ReactNode } from "react";

export type Props = {
  /** Handler for primary submit action */
  onSubmit: () => void;
  /** Handler for Google/OAuth action */
  onSubmitWithGoogle: () => void;
  /** Whether form is currently submitting */
  isSubmitting: boolean;
  /** Whether external data is loading */
  isLoading: boolean;
  /** Whether form is valid */
  isValid: boolean;
  /** Label for primary submit button */
  submitLabel: string;
  /** Label for Google button */
  googleLabel: string;
  /** Whether to show border */
  isWithBorder?: boolean;
  /** Whether to add padding */
  isWithPadding?: boolean;
  /** Container className */
  className?: string;
  /** Primary button className */
  signupButtonClassName?: string;
  /** Google button className */
  googleButtonClassName?: string;
  /** Render function for primary submit button */
  renderSubmitButton: (props: {
    onClick: () => void;
    disabled: boolean;
    visuallyDisabled: boolean;
    label: string;
    className?: string;
  }) => ReactNode;
  /** Render function for Google button */
  renderGoogleButton: (props: {
    onClick: () => void;
    label: string;
    className?: string;
  }) => ReactNode;
};

/**
 * Base auth actions component - unstyled, uses render props for flexibility.
 * Wrap with styled version in shared/features/auth.
 */
export function AuthActionsBase({
  className,
  onSubmit,
  onSubmitWithGoogle,
  isSubmitting,
  isLoading,
  isValid,
  submitLabel,
  googleLabel,
  signupButtonClassName,
  googleButtonClassName,
  renderSubmitButton,
  renderGoogleButton,
}: Props) {
  const isDisabled = isSubmitting || isLoading;

  return (
    <div className={className} data-testid="auth-actions">
      {renderSubmitButton({
        onClick: onSubmit,
        disabled: isDisabled,
        visuallyDisabled: !isDisabled && !isValid,
        label: submitLabel,
        className: signupButtonClassName,
      })}
      {renderGoogleButton({
        onClick: onSubmitWithGoogle,
        label: googleLabel,
        className: googleButtonClassName,
      })}
    </div>
  );
}

export default AuthActionsBase;
